import Pagination from "@kofile/react-components-pagination";
import classnames from "classnames";
import PropTypes from "prop-types";
import * as R from "ramda";
import React, { PureComponent } from "react";
import iconPrinter from "../../../public/assets/img/icon_printer.svg";
import { randomString, formatDate, isAbsent, isPresent } from "../../utils";
import * as Components from "../";
import { Content, Search, Info } from "./components";
import Totals from "./totals";

export class Report extends PureComponent {
  constructor(props) {
    super(props);

    const radioOptions = this.props.radioOptions ? this.props.radioOptions : [];
    const firstRadio = R.compose(R.propOr("", "value"), R.head)(radioOptions);

    this.state = {
      id: "",
      form: {
        date: {},
        radio: firstRadio,
      },
      hasFetched: !R.isEmpty(this.props.data),
    };

    this.updateFormState = (key) => (value) => {
      this.setState({
        form: {
          ...this.state.form,
          [key]: value,
        },
      });
    };

    this.setHasFetched = () => this.setState({ hasFetched: true });

    this.handleDatePicked = (pickedDate) => {
      const getDate = R.path(["form", "date"]);
      const changeState = R.not(R.equals(getDate(this.state), pickedDate));

      if (changeState) {
        this.updateFormState("date")(pickedDate);
      }
    };

    this.handleRadioChange = ({ target }) => {
      this.updateFormState("radio")(target.value);
    };

    this.handleSearch = ({
      date,
      radio = "",
      currentPage = 1,
      pageSize = 50,
    }) => {
      const { from: fromMoment, to: toMoment } = date;

      const format = {
        from: (fmt) => formatDate(fromMoment, fmt),
        to: (fmt) => formatDate(toMoment, fmt),
      };

      this.props.fetchData({
        from: format.from("YYYY-MM-DD"),
        to: format.to("YYYY-MM-DD"),
        groupBy: radio,
        correlationId: randomString(),
        currentPage,
        pageSize,
      });

      if (this.props.fetchTotals) {
        this.props.fetchTotals({
          from: format.from("YYYYMMDD"),
          to: format.to("YYYYMMDD"),
        });
      }

      this.setHasFetched();
    };

    this.handleSubmit = (formValues) => (event) => {
      event.preventDefault();

      this.handleSearch(formValues);
    };

    this.handlePrint = () => window.print();

    this.handlePageChange = (currentPage) => {
      this.handleSearch({ ...this.state.form, currentPage });
    };

    this.handlePrintKeyDown = (event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        this.handlePrint();
      }
    };

    this.handleCsvKeyDown = (event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        if (isPresent(this.props.csvUrl) && this.state.hasFetched) {
          event.target.click();
        }
      }
    };
  }

  getStatusMessage() {
    const { isLoading, error, data, pagination } = this.props;
    const { hasFetched } = this.state;
    const hasTotalPages = isPresent(pagination && pagination.totalPages);

    if (isLoading) {
      return "Loading report data...";
    }

    if (error) {
      return `Error loading report: ${error}`;
    }

    if (hasFetched && !isLoading && data) {
      const resultCount = data.length;
      const pageInfo = hasTotalPages
        ? ` You are on page ${pagination.currentPage} of ${pagination.totalPages}.`
        : "";
      return `Report loaded with ${resultCount} results.${pageInfo}`;
    }

    return "";
  }

  render() {
    const props = this.props;
    const state = this.state;
    const {
      data,
      isLoading,
      widgetTitle,
      searchTitle,
      searchDescription,
      iconPrinter,
      reportType,
      county,
      Table,
      radioOptions,
      pagination,
      csvUrl,
      error,
    } = props;

    const totalPages = R.prop("totalPages", pagination);
    const hasTotalPages = isPresent(totalPages);

    const { form, hasFetched } = state;
    const { date } = form;

    const dateIsPicked = R.not(R.isEmpty(date));
    const wrapperClassName = classnames("report-page", {
      stretch: !hasFetched,
    });

    const linkClassName = (shouldHide = false) =>
      classnames("report-page--link", {
        hide: !hasFetched || shouldHide,
      });

    const { from: fromMoment, to: toMoment } = date;
    const from = formatDate(fromMoment, "M/D/YYYY");
    const to = formatDate(toMoment, "M/D/YYYY");
    const searchTerm = `${from} - ${to}`;

    const searchInfo = (
      <Info
        countyName={county.name}
        searchMetrics={reportType}
        searchTerm={searchTerm}
      />
    );

    const onHeaderRangePicked = (date) => {
      this.handleDatePicked(date);
      this.handleSearch({ ...this.state.form, date });
    };

    const onHeaderRadioPicked = (event) => {
      const value = R.path(["target", "value"], event);

      this.handleRadioChange(event);
      this.handleSearch({ ...this.state.form, radio: value });
    };

    const radioProps = {
      options: radioOptions,
      onChange: this.handleRadioChange,
      value: this.state.form.radio,
    };

    const HeaderAction = () => (
      <div
        className="report-page__header-wrapper"
        role="toolbar"
        aria-label="Report filters and date range"
      >
        <div
          className="report-page__header-radio"
          role="group"
          aria-label="Report grouping options"
        >
          <Components.RadioSet {...radioProps} onChange={onHeaderRadioPicked} />
        </div>
        <div role="group" aria-label="Date range selection">
          <Components.DateRange
            handleRangePicked={onHeaderRangePicked}
            from={fromMoment}
            to={toMoment}
          />
        </div>
      </div>
    );

    const headerActionToRender = hasFetched && <HeaderAction />;

    const content = <Content Table={Table} data={data} isLoading={isLoading} />;

    const search = (
      <Search
        handleDatePicked={this.handleDatePicked}
        onSubmit={this.handleSubmit(this.state.form)}
        dateIsPicked={dateIsPicked}
        searchTitle={searchTitle}
        searchDescription={searchDescription}
        isLoading={isLoading}
        radioProps={radioProps}
      />
    );

    const contentToRender = hasFetched ? content : search;

    const statusMessage = this.getStatusMessage();

    return (
      <div
        className={wrapperClassName}
        role="main"
        aria-label="Report Dashboard"
      >
        {statusMessage && (
          <div
            className="sr-only"
            aria-live={error ? "assertive" : "polite"}
            aria-atomic="true"
            aria-label="Report status"
          >
            {statusMessage}
          </div>
        )}

        {searchInfo}

        <div
          className="report-page--controls"
          role="toolbar"
          aria-label="Report actions and navigation"
        >
          <Components.ReturnButton className="refunds__return-button" />

          <button
            className={linkClassName()}
            type="button"
            onClick={this.handlePrint}
            onKeyDown={this.handlePrintKeyDown}
            aria-label="Print current report page"
            aria-describedby="print-description"
          >
            <img
              src={iconPrinter}
              className="report-page--link-icon"
              alt=""
              aria-hidden="true"
            />
            Print This Page
          </button>
          <div id="print-description" className="sr-only">
            Opens the browser print dialog to print the current report page
          </div>

          {isPresent(csvUrl) && data.length > 0 && (
            <a
              href={csvUrl}
              className={linkClassName()}
              aria-label="Download complete report as CSV file"
              aria-describedby="csv-description"
              aria-disabled={isAbsent(csvUrl) || !hasFetched}
              download
              onKeyDown={this.handleCsvKeyDown}
              tabIndex={isAbsent(csvUrl) || !hasFetched ? -1 : 0}
            >
              Get Full Report CSV
            </a>
          )}
          <div id="csv-description" className="sr-only">
            Downloads the complete report data as a CSV file for use in
            spreadsheet applications
          </div>
        </div>

        {Array.isArray(props.totalsData) &&
          props.totalsData.length > 0 &&
          hasFetched && <Totals totals={props.totalsData} />}

        <Components.Widget
          className="dashboard-search full-widget"
          title={widgetTitle}
          action={headerActionToRender}
        >
          {contentToRender}
        </Components.Widget>

        {isPresent(pagination) && hasTotalPages && pagination.totalPages > 1 && (
          <nav
            role="navigation"
            aria-label="Report pagination"
            aria-describedby="pagination-description"
          >
            <div id="pagination-description" className="sr-only">
              Navigate through pages of report results
            </div>
            <Pagination
              currentPage={pagination.currentPage}
              totalPages={pagination.totalPages}
              handlePageChange={this.handlePageChange}
            />
          </nav>
        )}
      </div>
    );
  }
}

Report.displayName = "Report";

Report.propTypes = {
  data: PropTypes.arrayOf(PropTypes.array),
  tableConfig: PropTypes.shape({
    data: PropTypes.arrayOf(PropTypes.object),
    cellOptions: PropTypes.object,
    headerOptions: PropTypes.object,
  }),
  iconPrinter: PropTypes.string.isRequired,
  radioOptions: PropTypes.any,
  fetchData: PropTypes.any,
  error: PropTypes.string,
};

Report.defaultProps = {
  iconPrinter: iconPrinter,
};

export default Report;
